import type {
  IInfoPanelGroup,
  IInfoPanelItem,
} from '~/presentation/common/components/GInfoPanel/types';

import { useHeatmapFilterContext, useHeatmapFilterConfig } from '~/modules/heatmap';
import { EHeatmapFilterType } from '~/modules/heatmap/types';
import { GInfoPanelPopover } from '~/presentation/common';
import { PlusCircleIcon } from '@shopify/polaris-icons';
import { useTranslation } from 'react-i18next';
import { useMemo, useCallback } from 'react';

const GROUP_VISITOR = [EHeatmapFilterType.VISITOR_TYPE, EHeatmapFilterType.MARKET];
const GROUP_JOURNEY = [
  EHeatmapFilterType.VISITOR_JOURNEY,
  EHeatmapFilterType.TRAFFIC_SOURCE,
  EHeatmapFilterType.REFERRAL_SOURCE,
];
const GROUP_BEHAVIOR = [
  EHeatmapFilterType.PURCHASE_TYPE,
  EHeatmapFilterType.TIME_ON_PAGE,
  EHeatmapFilterType.ORDER_VALUE,
];
const GROUP_EXPERIMENT = [EHeatmapFilterType.CAMPAIGN];

export const PopoverAddFilter = () => {
  const draftFilters = useHeatmapFilterContext((s) => s.draftFilters);
  const setDraftFilter = useHeatmapFilterContext((s) => s.setDraftFilter);

  const { t } = useTranslation();
  const { filterConfigs, filterConfigMap } = useHeatmapFilterConfig();

  const addedFilterTypes = useMemo(() => {
    return new Set(Object.keys(draftFilters ?? {}) as EHeatmapFilterType[]);
  }, [draftFilters]);

  const filterItems: Record<EHeatmapFilterType, IInfoPanelItem> = useMemo(() => {
    return filterConfigs.reduce(
      (acc, config) => {
        acc[config.id] = {
          id: config.id,
          label: config.label,
          icon: config.icon,
          checkedIcon: addedFilterTypes.has(config.id),
          data: config.data,
          tags: config.tags,
          disabled: config.disabled,
        };
        return acc;
      },
      {} as Record<EHeatmapFilterType, IInfoPanelItem>,
    );
  }, [addedFilterTypes, filterConfigs]);

  const filterGroups = useMemo<IInfoPanelGroup[]>(() => {
    return [
      {
        groupLabel: t('Experiment'),
        items: GROUP_EXPERIMENT.map((id) => filterItems[id]).filter(Boolean),
      },
      {
        groupLabel: t('Visitor'),
        items: GROUP_VISITOR.map((id) => filterItems[id]).filter(Boolean),
      },
      {
        groupLabel: t('Journey'),
        items: GROUP_JOURNEY.map((id) => filterItems[id]).filter(Boolean),
      },
      {
        groupLabel: t('Behavior'),
        items: GROUP_BEHAVIOR.map((id) => filterItems[id]).filter(Boolean),
      },
    ];
  }, [filterItems, t]);

  const handleSelectFilter = useCallback(
    (filterId: string) => {
      const filterType = filterId as EHeatmapFilterType;
      const defaultValue = filterConfigMap[filterType]?.defaultValue ?? {};
      setDraftFilter(filterType, defaultValue);
    },
    [setDraftFilter, filterConfigMap],
  );

  return (
    <GInfoPanelPopover
      popoverProps={{
        fullWidth: true,
        autofocusTarget: 'first-node',
        preferredPosition: 'below',
        maxWidth: '448px',
        maxHeight: '422px',
      }}
      groups={filterGroups}
      searchEmpty={t('No options found.')}
      onSelect={handleSelectFilter}
      activatorIcon={PlusCircleIcon}
      activatorText={t('Add filter')}
      selectedId=""
      minWidth="unset"
      minHeight="min(454px, calc(100vh - 200px))"
      maxWidth="448px"
      preferredAlignment="left"
      columns="235px 1fr"
      defaultInfo={{
        title: t('Heatmap filter'),
        description: t('Show heatmap data based on specific conditions.'),
      }}
      search={{
        enabled: true,
        placeholder: t('Search filter'),
      }}
    />
  );
};
